import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Ban, FileSearch, Globe, QrCode, Scale, ShieldAlert, ShieldCheck } from 'lucide-react';
import { DMPLogo, DMP_LOGO_URL } from '../brand/DMPLogo';
import { citizenGuidelines, noticeMeta, sampleCase } from '../../data/sampleCase';
import type { CaseRecord } from '../../types/notice';
import { toBn } from '../../utils/bangla';
export interface NoticeTemplate {
  showLogo: boolean;
  headerLines: string[];
  memoFormat: string;
  qrPlacement: 'bottom-left' | 'top-right';
  subject: string;
  bodyText: string;
  noteText: string;
  showInstructions: boolean;
  showSignature: boolean;
  footer: string;
  templateVersion: string;
}
export const bodyPlaceholders: {
  token: string;
  label: string;
}[] = [{
  token: '{vehicle}',
  label: 'গাড়ি নং'
}, {
  token: '{caseDate}',
  label: 'মামলার তারিখ'
}, {
  token: '{caseTime}',
  label: 'মামলার সময়'
}, {
  token: '{location}',
  label: 'অপরাধের স্থান'
}, {
  token: '{offence}',
  label: 'অপরাধের ধরন'
}, {
  token: '{owner}',
  label: 'মালিকের নাম'
}, {
  token: '{law}',
  label: 'আইন/ধারা'
}, {
  token: '{hearingDate}',
  label: 'হাজিরার তারিখ'
}, {
  token: '{hearingTime}',
  label: 'হাজিরার সময়'
}];
export const defaultBody = {
  subject: 'সড়ক পরিবহন আইন ২০১৮ এর ১১৮ ধারা মোতাবেক চালক/মালিক উপস্থিত হওয়া প্রসঙ্গে।',
  bodyText: 'এই মর্মে আপনাকে অবগত করা যাচ্ছে যে, গাড়ি নং- {vehicle} গত {caseDate} তারিখ {caseTime} ঘটিকায় {location} এলাকায় ট্রাফিক আইন অমান্য ({offence}) করায় ডিএমপির AI-ভিত্তিক ট্রাফিক ক্যামেরার মাধ্যমে শনাক্ত হয়েছে। বিআরটিএ রেকর্ড অনুযায়ী আপনি উক্ত গাড়ির মালিক।\n\nসড়ক পরিবহন আইন, ২০১৮ এর ১১৮ ধারা অনুযায়ী আগামী {hearingDate} তারিখ {hearingTime} ঘটিকার মধ্যে গাড়ির ড্রাইভিং লাইসেন্স, রেজিস্ট্রেশন সার্টিফিকেট ও অন্যান্য প্রয়োজনীয় কাগজপত্রসহ নিম্নস্বাক্ষরকারীর কার্যালয়ে হাজির হয়ে প্রয়োজনীয় ব্যাখ্যা প্রদানের জন্য অনুরোধ করা হলো।',
  noteText: 'উল্লেখ্য যে, এ বিষয়ে সড়ক পরিবহন আইন ২০১৮ অনুযায়ী মামলা রুজু করার বিষয়টি প্রক্রিয়াধীন রয়েছে।'
};
export const defaultTemplate: NoticeTemplate = {
  showLogo: true,
  headerLines: ['গণপ্রজাতন্ত্রী বাংলাদেশ সরকার', 'বাংলাদেশ পুলিশ', 'ঢাকা মেট্রোপলিটন পুলিশ হেডকোয়ার্টার্স', '৩৬, শহীদ ক্যাপ্টেন মনসুর আলী সরণি, রমনা, ঢাকা-১০০০', 'ওয়েবসাইট: www.dmp.gov.bd'],
  memoFormat: 'স্মারক নং- {memo}',
  qrPlacement: 'top-right',
  ...defaultBody,
  showInstructions: true,
  showSignature: true,
  footer: 'সচেতন নাগরিক হোন, নিরাপদ সড়ক গড়ে তুলুন',
  templateVersion: 'T-2025.06 (v3)'
};
const instructionIcons = [QrCode, Globe, Ban, FileSearch, ShieldAlert, Scale];
function fill(text: string, d: CaseRecord): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => k in d ? String(d[k as keyof CaseRecord]) : m);
}
interface SecureNoticeProps {
  data?: CaseRecord;
  version?: number;
  showQr?: boolean;
  watermark?: boolean;
  template?: NoticeTemplate;
  highlightQr?: boolean;
}
export function SecureNotice({
  data = sampleCase,
  version = 1,
  showQr = true,
  watermark = true,
  template = defaultTemplate,
  highlightQr = false
}: SecureNoticeProps) {
  const docId = `DMP-DOC-2025-0748-V${version}`;
  const left: [string, string, string][] = [['কেস নং', 'Case No.', data.caseNo], ['কেস তারিখ', 'Case Date', data.caseDate], ['কেস সময়', 'Case Time', data.caseTime], ['কেস ধরন', 'Case Type', data.offence], ['গাড়ি নং', 'Vehicle No.', data.vehicle]];
  const right: [string, string, string][] = [['মালিকের নাম', 'Owner Name', data.owner], ['মোবাইল নং', 'Mobile No.', data.mobile], ['অপরাধের স্থান', 'Location', data.location], ['আইনের ধারা', 'Legal Section', data.law], ['শুনানির তারিখ', 'Hearing Date', data.hearingDate], ['শুনানির সময়', 'Hearing Time', data.hearingTime]];
  const qrBlock = <div className={`w-[200px] shrink-0 overflow-hidden rounded-md border border-[#003B71] bg-white ${highlightQr ? 'ring-4 ring-[#E69A20]/60' : ''}`} data-id="element-270" data-mp="1hffkrqec9ygr">
      <div className="bg-[#003B71] px-2 py-1 text-center font-sans text-[11.5px] font-semibold text-white" data-id="element-271" data-mp="2svnnbrtrt9sf">নোটিশ যাচাই করুন (QR Code)</div>
      <div className="px-3 pb-2 pt-1.5 text-center" data-id="element-272" data-mp="5qcg1qzuzuqv">
        <div className="text-[10.5px] leading-tight text-slate-600" data-id="element-273" data-mp="qg8jhrydl6k">এই নোটিশটি যাচাই করতে QR কোড স্ক্যান করুন</div>
        <div className="mx-auto mt-1.5 flex h-[118px] w-[118px] items-center justify-center rounded border border-slate-200 bg-white" data-id="element-274" data-mp="eqr24sknddvf">
          {showQr ? <QRCodeSVG value={noticeMeta.qrValue} size={110} level="H" fgColor="#002a52" imageSettings={{
          src: DMP_LOGO_URL,
          height: 26,
          width: 26,
          excavate: true
        }} data-id="element-275" data-mp="omu2bsvxv2fq" /> : <span className="px-2 text-center text-[11px] text-slate-400" data-id="element-276" data-mp="xhi9jol2qu76">QR তৈরি হয়নি</span>}
        </div>
        <div className="mt-1.5 text-[10.5px] text-slate-600" data-id="element-277" data-mp="17dlm7odjyb8d">যাচাই লিংক (প্রোটোটাইপ)</div>
        <div className="break-all font-mono text-[9.5px] text-[#1769AA]" data-id="element-278" data-mp="12es7jsmj4e6m">{noticeMeta.verifyUrlDisplay}</div>
        <div className="mt-1 font-mono text-[10px] font-semibold text-slate-900" data-id="element-279" data-mp="1m0tpm30mdc9s">Verification ID: {noticeMeta.verificationId}</div>
      </div>
    </div>;
  const Cell = ({
    row
  }: {
    row: [string, string, string];
  }) => <>
      <th className="w-[26%] border border-slate-300 bg-[#EEF3F9] px-2 py-1.5 text-left align-top font-semibold text-[#003B71]" data-id="element-280" data-mp="1c1cn1ugnzei1">
        {row[0]} <span className="font-sans text-[10px] font-normal text-slate-500" data-id="element-281" data-mp="1h3k4v0o6dosa">({row[1]})</span>
      </th>
      <td className={`w-[24%] border border-slate-300 px-2 py-1.5 align-top ${row[1] === 'Case No.' ? 'font-mono text-[11.5px] font-semibold' : ''}`} data-id="element-282" data-mp="137zlk44jb4kc">{row[2]}</td>
    </>;
  return <article className="print-area relative flex flex-col overflow-hidden bg-white font-serifbn text-[13px] leading-relaxed text-slate-900 shadow-sm" style={{
    width: 794,
    minHeight: 1123,
    padding: '30px 40px 0'
  }} data-id="element-283" data-mp="tdpt6mfle69m">
      {watermark && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden data-id="element-284" data-mp="ocjwyemq40vc">
          <span className="rotate-[-32deg] select-none text-[120px] font-bold text-[#003B71] opacity-[0.045]" data-id="element-285" data-mp="2qzz1vb5cys0n">নমুনা নোটিশ</span>
        </div>}

      {/* Letterhead */}
      <header className="relative flex items-start" data-id="element-286" data-mp="2m6xkmwhoigdw">
        <div className="w-[96px]" data-id="element-287" data-mp="2hafi839tmu4q">{template.showLogo && <DMPLogo size={76} data-id="element-288" data-mp="2cakqjxi820im" />}</div>
        <div className="flex-1 text-center leading-snug" data-id="element-289" data-mp="2cbccshpxilw8">
          {template.headerLines.map((l, i) => <div key={`${l}-${i}`} className={i === 2 ? 'text-[21px] font-bold text-[#003B71]' : i === 1 ? 'text-[15px] font-semibold' : 'text-[12.5px]'} data-id="element-290" data-mp="39x9vaf4b6r86" data-mpi={i}>
              {l}
            </div>)}
        </div>
        <div className="w-[96px]" data-id="element-291" data-mp="2zykfqszvxiv3" />
      </header>

      <div className="mt-2 flex items-end justify-between text-[13px]" data-id="element-292" data-mp="2vytq4sm3h8s5">
        <span className="font-semibold" data-id="element-293" data-mp="2w0wg5cn8z1wv">{template.memoFormat.replace('{memo}', noticeMeta.memoNo)}</span>
        <span data-id="element-294" data-mp="1oinh2d28vkko">তারিখ: {noticeMeta.issueDate}</span>
      </div>
      <div className="mt-1 h-[2px] bg-[#003B71]" data-id="element-295" data-mp="1khtq1ati9pnk" />

      {/* Recipient + QR */}
      <div className="mt-3 flex items-start justify-between gap-6" data-id="element-296" data-mp="2dsve6o9she">
        <div className="pt-1" data-id="element-297" data-mp="42o8lik9nx9g">
          <div data-id="element-298" data-mp="43zatdy2cwyg">প্রতি,</div>
          <div className="font-semibold" data-id="element-299" data-mp="437h68o119at">{data.owner}</div>
          <div data-id="element-300" data-mp="91sg84rjlmxo">{data.address}</div>
          <div data-id="element-301" data-mp="90hba1md294z">মোবাইল: {data.mobile}</div>
        </div>
        {template.qrPlacement === 'top-right' && qrBlock}
      </div>

      {/* Subject & body */}
      <div className="mt-3" data-id="element-302" data-mp="isdefr3b0pg2">
        <span className="font-bold" data-id="element-303" data-mp="ir2anr9uzhoy">বিষয়: </span>
        <span className="font-semibold underline decoration-slate-500 underline-offset-4" data-id="element-304" data-mp="nqwzlpa2px7d">{fill(template.subject, data)}</span>
      </div>
      {template.bodyText.split(/\n\s*\n/).filter(Boolean).map((p, i) => <p key={i} className="mt-2 text-justify indent-8" data-id="element-305" data-mp="ws4xw7clay1g" data-mpi={i}>{fill(p.trim(), data)}</p>)}
      {template.noteText.trim() && <p className="mt-2 font-bold" data-id="element-306" data-mp="2tkyfarkbfm31">{fill(template.noteText, data)}</p>}

      {/* Case details */}
      <section className="mt-3 overflow-hidden rounded-sm border border-[#003B71]" data-id="element-307" data-mp="2yk16p97dhtsj">
        <div className="bg-[#003B71] px-3 py-1 text-[13px] font-semibold text-white" data-id="element-308" data-mp="14acjqwmw8hrw">মামলার তথ্য <span className="font-sans text-[11px] font-normal" data-id="element-309" data-mp="109sq431j3wai">(Case Details)</span></div>
        <table className="w-full border-collapse text-[12px]" data-id="element-310" data-mp="1e0xoz2x2okjs">
          <tbody data-id="element-311" data-mp="1e05u5ubauquk">
            {right.map((r, i) => <tr key={r[1]} data-id="element-312" data-mp="191cje9neddy3" data-mpi={i}>
                {left[i] ? <Cell row={left[i]} data-id="element-313" data-mp="192mh2xqc8k47" /> : <><th className="border border-slate-300 bg-[#EEF3F9]" data-id="element-314" data-mp="194731yyl6uqg" /><td className="border border-slate-300" data-id="element-315" data-mp="ld1mtioqi0fm" /></>}
                <Cell row={r} data-id="element-316" data-mp="hay88l6rqfq4" />
              </tr>)}
          </tbody>
        </table>
      </section>

      {/* Contact + signature */}
      <div className="mt-3 flex items-end justify-between gap-6 text-[12px]" data-id="element-317" data-mp="vc0v305l7u23">
        <div className="flex items-end gap-4" data-id="element-318" data-mp="qabhpduw74au">
          {template.qrPlacement === 'bottom-left' && qrBlock}
          <div data-id="element-319" data-mp="26obfai69mhux">
            <div className="font-semibold" data-id="element-320" data-mp="26nryj4scpzih">যোগাযোগের ঠিকানা:</div>
            <div data-id="element-321" data-mp="21pgniqqbliyo">উপ-পুলিশ কমিশনার (ট্রাফিক-তেজগাঁও বিভাগ)</div>
            <div data-id="element-322" data-mp="3jo4xhgkjjnn6">এর কার্যালয়, ডিএমপি, ঢাকা।</div>
            <div data-id="element-323" data-mp="3jndct0shfabo">ফোন: ০১৩২০০৪৪৬০৮</div>
          </div>
        </div>
        {template.showSignature && <div className="w-[220px] text-center" data-id="element-324" data-mp="3oosy8tykniul">
            <svg viewBox="0 0 200 50" className="mx-auto h-10 w-36" aria-hidden data-id="element-325" data-mp="3anqa8bo0nyh1">
              <path d="M8 34c18-22 28 10 42-6s14-16 24 2 20-14 32-4 12 14 30 0 26-8 52-2" fill="none" stroke="#1f3b8a" strokeWidth="2.2" strokeLinecap="round" data-id="element-326" data-mp="3apt97l75d1xw" />
            </svg>
            <div className="text-[10.5px] text-slate-500" data-id="element-327" data-mp="77jf4tjo39jv">{noticeMeta.issueDate}</div>
            <div className="border-t border-slate-400 pt-0.5 font-semibold" data-id="element-328" data-mp="b7tmoan3y80u">{noticeMeta.signatory.title}</div>
            <div data-id="element-329" data-mp="3tm2kk0qnzee7">{noticeMeta.signatory.dept}</div>
            <div data-id="element-330" data-mp="2fhv2lg9ceja">ফোন- ০১৩২০০৪৪৬০৮</div>
            <div className="font-sans text-[10.5px]" data-id="element-331" data-mp="2egmcnmps44q">ই-মেইল: trafficmedia@gmail.com</div>
            <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#E7F4EC] px-2 py-0.5 font-sans text-[9.5px] text-[#16834A]" data-id="element-332" data-mp="q4u4nmfhyom4">
              <ShieldCheck className="h-3 w-3" data-id="element-333" data-mp="2ytukcebnsfal" /> ডিজিটালি স্বাক্ষরিত
            </div>
          </div>}
      </div>

      {/* Citizen instructions */}
      {template.showInstructions && <section className="mt-3 overflow-hidden rounded-sm border border-[#003B71]/50" data-id="element-334" data-mp="33qkw4pgfkoq7">
          <div className="bg-[#003B71] px-3 py-1 text-center text-[13px] font-semibold text-white" data-id="element-335" data-mp="2pvikdlowk55h">নাগরিকের জন্য নির্দেশনা <span className="font-sans text-[11px] font-normal" data-id="element-336" data-mp="2g3ctkfadztrv">(Citizen Instructions)</span></div>
          <ol className="grid grid-cols-2 gap-x-5 gap-y-1.5 bg-[#F4F7FB] px-3 py-2.5 text-[11.5px] leading-snug" data-id="element-337" data-mp="2ku2y5qaznsli">
            {citizenGuidelines.map((g, i) => {
          const Icon = instructionIcons[i] ?? ShieldCheck;
          return <li key={g} className="flex items-center gap-2" data-id="element-338" data-mp="2b51loa3njtca" data-mpi={i}>
                  <span className="w-5 shrink-0 font-sans text-[13px] font-bold text-[#003B71]" data-id="element-339" data-mp="1x3yvpm9jdyw3">{toBn(`0${i + 1}`)}</span>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#003B71] text-white" data-id="element-340" data-mp="222jy9sc9gunp"><Icon className="h-3.5 w-3.5" data-id="element-341" data-mp="1iiuvfncjnstw" /></span>
                  <span data-id="element-342" data-mp="1dioz3tvbxi5h">{g}</span>
                </li>;
        })}
          </ol>
        </section>}

      <div className="mt-2 flex justify-between font-sans text-[9.5px] text-slate-500" data-id="element-343" data-mp="1riq9bzxhi1xm">
        <span data-id="element-344" data-mp="1ngd4ddqka81w">Document ID: <span className="font-mono" data-id="element-345" data-mp="1ngmsevs1q54y">{docId}</span> • টেমপ্লেট: {template.templateVersion}</span>
        <span data-id="element-346" data-mp="21fvyv3qg9kvq">প্রোটোটাইপ প্রদর্শনী — সকল তথ্য নমুনা</span>
      </div>

      <div className="h-3 shrink-0" data-id="element-347" data-mp="1whkpeu42hce1" />
      {/* Footer bar */}
      <footer className="-mx-10 mt-auto flex items-center justify-between bg-[#003B71] px-10 py-2.5 text-[13px] font-semibold text-white" data-id="element-348" data-mp="1whaxv23dix68">
        <span data-id="element-349" data-mp="267mffrfzd4vl">{template.footer}</span>
        <span data-id="element-350" data-mp="268p3zsptyv8c">ডিএমপি ট্রাফিক বিভাগ</span>
      </footer>
    </article>;
}